import { useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import type { GetApplicationResponse } from '@ab/ams-school-api/types/private/v1';

/**
 * Dismisses the active document request for the application.
 * On success, invalidates the application query so the UI updates.
 */
export function useDismissDocumentRequest(applicationId: string) {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, mutateAsync } = useMutation({
    mutationKey: ['dismissDocumentRequest', applicationId],
    mutationFn: async () => {
      const url = `${config.apiHost}/private/v1/applications/${applicationId}/additional_document_requests/dismiss`;
      const response = await request<GetApplicationResponse>(url, {
        method: 'POST',
        body: {},
        isExpectedResponse: (res): res is GetApplicationResponse =>
          Boolean(res && typeof res === 'object' && 'data' in res),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
    },
  });

  return {
    dismissDocumentRequest: mutateAsync,
    isDismissingDocumentRequest: isLoading,
  };
}
